import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOutIcon, MenuIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import type { PortalMe } from '@/api/types'
import { requirePortal } from '@/auth/guards'
import { signOutEverywhere } from '@/auth/session'
import { useCurrentUser } from '@/auth/use-auth'
import { BrandMark } from '@/components/brand-mark'
import { ClientAvatar } from '@/components/clients/client-avatar'
import { PortalBell } from '@/components/portal/portal-bell'
import { PortalClosed } from '@/components/portal/portal-closed'
import { PortalSidebarNav } from '@/components/portal/portal-sidebar'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/user-avatar'
import { usePortalBookingHosts } from '@/hooks/use-portal-booking'
import { usePortalTodo } from '@/hooks/use-portal-inbox'
import { usePortalSecrets } from '@/hooks/use-portal-secrets'
import { browserLocale, msg, setCurrentLocale } from '@/i18n'
import { portalLocale } from '@/i18n/portal-language'
import { isHexColor } from '@/lib/client-color'
import { PORTAL_TRAININGS_KEY } from '@/lib/training-video'

export const Route = createFileRoute('/portal')({
  beforeLoad: requirePortal,
  component: PortalLayout,
})

function PortalLayout() {
  const navigate = useNavigate()
  const user = useCurrentUser()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  // La hauteur de l'en-tête collant, pour ce qui se colle dessous (#296 : la
  // barre de progression d'un questionnaire). Elle varie avec le filet
  // d'accent (#346 : la navigation est passée dans le menu latéral).
  useEffect(() => {
    const header = headerRef.current
    if (!header) return
    const publish = () => {
      document.documentElement.style.setProperty(
        '--portal-header-h',
        `${String(header.offsetHeight)}px`,
      )
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(header)
    return () => {
      observer.disconnect()
    }
  })
  // Une page ouverte depuis le tiroir le referme, même par un lien du contenu.
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  const me = useQuery({
    queryKey: ['portal', 'me'],
    queryFn: () => portalApi.me(),
    // Un espace fermé le reste : réessayer ne ferait que répéter le 403.
    retry: (count, error) => !isClosed(error) && count < 2,
  })
  // Lot 28 : le badge d'« Accueil » est le total servi de « À faire pour vous ».
  const todo = usePortalTodo({ enabled: me.isSuccess })
  const todoTotal = todo.data?.total ?? 0
  const bookingHosts = usePortalBookingHosts()
  const canBook = (bookingHosts.data?.length ?? 0) > 0
  const sharedSecrets = usePortalSecrets(me.isSuccess)
  // 1.84.0 : un accès demandé ouvre aussi « Accès partagés », où il se saisit.
  const hasSecrets =
    (sharedSecrets.data?.length ?? 0) > 0 ||
    (todo.data?.items ?? []).some((item) => item.kind === 'secret_request')
  // Lot 38d : « Formations » au menu dès qu'une vidéo est publiée pour le client.
  const trainings = useQuery({
    queryKey: PORTAL_TRAININGS_KEY,
    queryFn: () => portalApi.trainings(),
    enabled: me.isSuccess,
  })
  const hasTrainings = (trainings.data?.length ?? 0) > 0

  // L'espace fermé remplace tout le cadre, pas seulement son contenu : la
  // navigation et « Nouvelle demande » mèneraient à des pages qui refusent
  // toutes, et proposer d'écrire ici serait une promesse qu'on ne tient pas.
  if (isClosed(me.error)) {
    // La personne n'est plus lisible : la langue de son navigateur.
    setCurrentLocale(portalLocale(browserLocale()))
    return <PortalClosed />
  }

  // Tant qu'on ne sait pas si l'espace est ouvert, on ne montre ni navigation
  // ni contenu : quelqu'un dont la relation a pris fin voyait sinon son
  // portail fonctionner une demi-seconde avant qu'on le lui retire.
  if (me.isPending) return <PortalFrame />

  // Lot 34 : la langue du contact, posée avant de rendre le cadre et ses pages.
  const locale = portalLocale(me.data?.language)
  setCurrentLocale(locale)
  const t = msg().portalShell
  const navState = {
    todoTotal,
    showTime: me.data?.show_time ?? false,
    canBook,
    hasSecrets,
    hasTrainings,
  }

  return (
    <div className="flex min-h-dvh">
      {/* #346 : le menu latéral, comme celui de l'équipe — colonne fixe au bureau. */}
      <aside className="on-steel sticky top-0 hidden h-dvh w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground md:flex print:hidden">
        <PortalSidebarHead client={me.data?.client} />
        <PortalSidebarNav state={navState} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* L'en-tête garde la cloche et le compte ; le reste est dans le menu. */}
        <header
          ref={headerRef}
          className="on-steel sticky top-0 z-30 bg-steel text-steel-foreground print:hidden"
        >
          {/* 1.60.0 : la couleur du client, un filet d'accent — jamais du texte posé dessus. */}
          {isHexColor(me.data?.client.color) && (
            <div
              aria-hidden="true"
              data-testid="accent-client-telephone"
              className="h-1 w-full md:hidden"
              style={{ backgroundColor: me.data.client.color }}
            />
          )}
          <div className="flex h-14 w-full items-center gap-2 px-3 sm:px-6 lg:px-8 xl:px-10">
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  aria-label={t.openMenu}
                  data-testid="ouvrir-menu-portail"
                >
                  <MenuIcon />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="on-steel w-72 gap-0 bg-sidebar p-0 text-sidebar-foreground"
                data-testid="tiroir-portail"
              >
                <SheetTitle className="sr-only">{t.menuTitle}</SheetTitle>
                <PortalSidebarHead client={me.data?.client} />
                <PortalSidebarNav
                  state={navState}
                  onNavigate={() => {
                    setDrawerOpen(false)
                  }}
                />
              </SheetContent>
            </Sheet>

            <Link
              to="/portal"
              className="flex min-w-0 items-center gap-2 rounded-md focus-visible:outline-2 focus-visible:outline-steel-foreground md:hidden"
            >
              {me.data ? (
                <ClientAvatar client={me.data.client} className="size-7 text-xs" />
              ) : (
                <BrandMark className="size-7" />
              )}
              <span className="truncate text-sm font-semibold">{me.data?.client.name}</span>
              <span className="sr-only">{t.homeLink}</span>
            </Link>

            <div className="ml-auto flex items-center gap-1.5">
              <PortalBell />
              <UserAvatar user={user} />
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await signOutEverywhere()
                  await navigate({ to: '/login', search: { redirect: undefined } })
                }}
              >
                <LogOutIcon />
                <span className="hidden sm:inline">{msg().common.actions.signOut}</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-3 py-4 sm:px-6 sm:py-5 lg:px-8 xl:px-10">
          {/* Une autre langue remonte les pages : elles relisent leurs phrases. */}
          <Outlet key={locale} />
        </main>

        <footer className="px-4 py-4 text-center text-xs text-muted-foreground sm:px-6 print:hidden">
          © {new Date().getFullYear()} Oyenga
        </footer>
      </div>
    </div>
  )
}

/**
 * La tête du menu (#346) : le filet et le logo du client (1.60.0), son nom, et
 * « Espace client Oyenga ». Un lien vers l'accueil.
 */
function PortalSidebarHead({ client }: { client: PortalMe['client'] | undefined }) {
  const t = msg().portalShell
  return (
    <div className="border-b border-sidebar-border" data-testid="tete-menu-portail">
      {isHexColor(client?.color) && (
        <div
          aria-hidden="true"
          data-testid="accent-client"
          className="h-1 w-full"
          style={{ backgroundColor: client.color }}
        />
      )}
      <Link
        to="/portal"
        className="flex h-14 items-center gap-2.5 px-4 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-steel-foreground"
      >
        {client ? (
          <ClientAvatar client={client} className="size-8 text-xs" />
        ) : (
          <BrandMark className="size-7" />
        )}
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{client?.name}</span>
          <span className="block text-xs text-sidebar-foreground/70">{t.space}</span>
        </span>
        <span className="sr-only">{t.homeLink}</span>
      </Link>
    </div>
  )
}

/** Le serveur nomme la situation : on la distingue d'un refus ordinaire. */
function isClosed(error: unknown): boolean {
  return error instanceof ApiError && error.reason === 'client_archived'
}

/** Cadre neutre : la marque et rien d'autre, le temps de savoir. */
function PortalFrame() {
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="on-steel bg-steel text-steel-foreground">
        <div className="flex h-14 w-full items-center gap-2 px-4 sm:px-6 lg:px-8 xl:px-10">
          <BrandMark className="size-7" />
          <p className="text-sm font-semibold">{msg().portalShell.space}</p>
        </div>
      </header>
      <main className="flex-1 space-y-5 px-4 py-5 sm:px-6 lg:px-8 xl:px-10">
        <Skeleton className="h-8 w-56 rounded-md" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </main>
    </div>
  )
}
